import { FlexColumn, FlexRow, StyledBanner, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { Delete } from 'baseui/icon';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { HeadingXSmall } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { generatePath, useNavigate } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES, INSTALLATIONS_DETAIL_PAGE } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createInstallation } from '../../../services/installationService';
import {
  editInstallationErrorState,
  editInstallationState,
  editInstallationTouchedState,
} from '../../../storage/Installations';
import { Brand } from '../../../types/brand';
import { InstallationRequest } from '../../../types/installation';
import { InstallationForm } from '../InstallationForm';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  defaultValues?: InstallationRequest;
  defaultBrand?: Brand;
  catchSubmission?: (form: InstallationRequest) => void;
};
function CreateInstallationModal({ onClose, isOpen, defaultValues, defaultBrand, catchSubmission }: Props) {
  const { theme, classes, css } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const editInstallation = useRecoilValue(editInstallationState);
  const formHasError = useRecoilValue(editInstallationErrorState);
  const resetEditInstallationState = useResetRecoilState(editInstallationState);
  const resetEditInstallationTouchedState = useResetRecoilState(editInstallationTouchedState);
  const { showPositiveFeedback, showFailFeedback } = useFeedback();
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isDerived = !!catchSubmission;
  const canSubmit = !formHasError;

  useEffect(() => {
    return () => {
      setTimeout(() => {
        clearStates();
      }, 300);
    };
  }, [isOpen]);

  const clearStates = (update?: boolean) => {
    resetEditInstallationState();
    resetEditInstallationTouchedState();
  };

  const onSubmit = async () => {
    if (!editInstallation) return;
    startLoading();
    const { error, id } = await createInstallation(editInstallation);
    if (!error) {
      showPositiveFeedback(FEEDBACK.created(FEEDBACK_PREFIXES.emission));
      if (id) {
        const route = generatePath(INSTALLATIONS_DETAIL_PAGE.route, { installationId: id });
        navigate(route, { replace: false });
      }
      onClose();
    } else {
      setError(typeof error === 'string' ? error : error.toString());
      showFailFeedback(error);
    }
    stopLoading();
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={() => onClose()}
      isOpen={isOpen}
      size="auto"
    >
      <ModalHeader>
        <FlexRow
          gap={theme.sizing.scale200}
          classNames={classes.centeredStart}
        >
          <HeadingXSmall margin={0}>Nueva emisión</HeadingXSmall>
        </FlexRow>
      </ModalHeader>
      <ModalBody>
        <FlexColumn
          classNames={css({
            width: '75vw',
            maxWidth: '1200px',
            minWidth: '350px',
            maxHeight: '80vh',
            overflow: 'auto',
          })}
        >
          {error && (
            <StyledBanner
              title="*Error*"
              kind="negative"
              action={{
                label: 'label',
                icon: () => <Delete />,
                onClick: () => {
                  setError('');
                },
              }}
            >
              {error}
            </StyledBanner>
          )}
          <InstallationForm
            defaultValues={defaultValues}
            defaultBrand={defaultBrand}
          />
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={classes.centeredEnd}
        >
          <StyledButton
            kind="tertiary"
            onClick={() => onClose()}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={isDerived ? () => catchSubmission(editInstallation) : onSubmit}
            disabled={!canSubmit}
            isLoading={loading}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body
  );
}

export default memo(CreateInstallationModal);
